import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Box, Paper, Typography, Button, Chip, Divider,
  Radio, RadioGroup, FormControlLabel, Alert, TextField,
  Tab, Tabs, IconButton, Tooltip as MuiTooltip,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import TuneIcon from "@mui/icons-material/Tune";
import ReplayIcon from "@mui/icons-material/Replay";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import * as XLSX from "xlsx";
import { MOCK_ZONAS } from "../../api/mockCalidadDatos";
import api, { axiosPrivate } from "../../api/axios";
import PageBody from "../GeneralComponents/PageBody";
import PageButtonBlack from "../GeneralComponents/PageButtonBlack";
import InputFilter from "../GeneralComponents/InputFilter";
import HourglassLoader from "../GeneralComponents/HourglassLoader";

const OP_COLORS = [
  "#4CAF50", "#1976D2", "#FF9800", "#9C27B0", "#795548",
  "#F44336", "#42A5F5", "#263238", "#388E3C", "#00897B",
  "#FF5722", "#78909C",
];

const METRICS = [
  {
    key: "subida",
    label: "Tráfico promedio de subida",
    unit: "Mb/s",
    description: "Evolución del tráfico promedio de subida por operador en la ventana seleccionada.",
    convert: (v) => (v * 8) / 1e6,
  },
  {
    key: "bajada",
    label: "Tráfico promedio de bajada",
    unit: "Mb/s",
    description: "Evolución del tráfico promedio de bajada por operador en la ventana seleccionada.",
    convert: (v) => (v * 8) / 1e6,
  },
  {
    key: "interac_bajada",
    label: "Tráfico total de bajada internacional",
    unit: "Mb/s",
    description: "Evolución del tráfico de bajada internacional agregado por operador.",
    convert: (v) => (v * 8) / 1e6,
  },
  {
    key: "latencia",
    label: "Latencia",
    unit: "ms",
    description: "Evolución de la latencia promedio por operador en la ventana seleccionada.",
    convert: (v) => v,
  },
  {
    key: "perdida_paquetes",
    label: "Pérdida de paquetes",
    unit: "%",
    description: "Evolución de la pérdida de paquetes promedio por operador en la ventana seleccionada.",
    convert: (v) => v,
  },
];

const GROUP_DIM_OPTIONS = [
  { key: "zona",         label: "Zona"       },
  { key: "operador",     label: "Operador"   },
  { key: "global",       label: "Global"     },
  { key: "zona_tec",     label: "Zona/Tec"   },
  { key: "operador_tec", label: "Op/Tec"     },
  { key: "global_tec",   label: "Global/Tec" },
];

// Filtros que se desactivan según la dimensión seleccionada (tab GRUPO).
const DIM_DISABLED = {
  zona:         { operador: false, zona: false, tec: true  },
  operador:     { operador: false, zona: true,  tec: true  },
  global:       { operador: true,  zona: true,  tec: true  },
  zona_tec:     { operador: false, zona: false, tec: false },
  operador_tec: { operador: false, zona: true,  tec: false },
  global_tec:   { operador: true,  zona: true,  tec: false },
};
const NO_DISABLED = { operador: false, zona: false, tec: false };

const MAX_RBDS = 5;

const toDateStr     = (d) => d.toISOString().split("T")[0];
const daysAgo       = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return toDateStr(d); };
const monthLastDay  = (ym) => { const [y, m] = ym.split("-").map(Number); return toDateStr(new Date(y, m, 0)); };
const monthsAgoFirst = (n) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - n); return toDateStr(d); };
const monthsBetween = (a, b) => { const [ay, am] = a.slice(0, 7).split("-").map(Number); const [by, bm] = b.slice(0, 7).split("-").map(Number); return (by - ay) * 12 + (bm - am); };

// Texto del tooltip de ayuda del selector de granularidad.
const GRAN_HELP = "Cantidad de puntos en la métrica";

// Oculta el filtro de Ventana. Con el filtro oculto se consulta siempre "dia".
const SHOW_VENTANA_FILTER = false;

const GRAN_CONFIG = {
  "15m": {
    label: "15 min",
    presets: [
      { label: "Hoy",  compute: () => { const t = toDateStr(new Date()); return { ini: t, fin: t }; } },
      { label: "Ayer", compute: () => { const t = daysAgo(1);            return { ini: t, fin: t }; } },
    ],
    getDefaultRange: () => { const t = toDateStr(new Date()); return { ini: t, fin: t }; },
    apiUrl: "/service/reportes/supervision/cq",
    apiGran: "15m",
  },
  "1h": {
    label: "1 hora",
    presets: [
      { label: "Hoy",  compute: () => { const t = toDateStr(new Date()); return { ini: t, fin: t }; } },
      { label: "Ayer", compute: () => { const t = daysAgo(1);            return { ini: t, fin: t }; } },
    ],
    getDefaultRange: () => { const t = toDateStr(new Date()); return { ini: t, fin: t }; },
    apiUrl: "/service/reportes/supervision/cq",
    apiGran: "1h",
  },
  "1d": {
    label: "1 día",
    presets: [
      { label: "3 días",   compute: () => ({ ini: daysAgo(3),  fin: toDateStr(new Date()) }) },
      { label: "1 semana", compute: () => ({ ini: daysAgo(7),  fin: toDateStr(new Date()) }) },
      { label: "1 mes",    compute: () => ({ ini: daysAgo(30), fin: toDateStr(new Date()) }) },
      { label: "3 meses",  compute: () => ({ ini: daysAgo(90), fin: toDateStr(new Date()) }) },
    ],
    getDefaultRange: () => ({ ini: daysAgo(3), fin: toDateStr(new Date()) }),
    apiUrl: "/service/reportes/supervision/cq",
    apiGran: "1d",
  },
  "7d": {
    label: "7 días",
    presets: [
      { label: "1 mes",    compute: () => ({ ini: monthsAgoFirst(1), fin: toDateStr(new Date()) }) },
      { label: "3 meses",  compute: () => ({ ini: monthsAgoFirst(3), fin: toDateStr(new Date()) }) },
      { label: "6 meses",  compute: () => ({ ini: monthsAgoFirst(6), fin: toDateStr(new Date()) }) },
    ],
    getDefaultRange: () => ({ ini: monthsAgoFirst(3), fin: toDateStr(new Date()) }),
    apiUrl: "/service/reportes/stats-rbd/agregado",
    apiGran: "7d",
  },
  "30d": {
    label: "1 mes",
    presets: [
      { label: "1 mes",    compute: () => ({ ini: monthsAgoFirst(1), fin: toDateStr(new Date()) }) },
      { label: "3 meses",  compute: () => ({ ini: monthsAgoFirst(3), fin: toDateStr(new Date()) }) },
      { label: "6 meses",  compute: () => ({ ini: monthsAgoFirst(6), fin: toDateStr(new Date()) }) },
    ],
    getDefaultRange: () => ({ ini: monthsAgoFirst(6), fin: toDateStr(new Date()) }),
    apiUrl: "/service/reportes/stats-rbd/agregado",
    apiGran: "30d",
  },
};

function buildChartData(filteredRows, metric, ventana, getKey) {
  const colName = `${metric.key}_${ventana}_mean`;

  const byDate = {};
  filteredRows.forEach((r) => {
    const k = getKey(r);
    if (!byDate[r.fecha]) byDate[r.fecha] = {};
    if (!byDate[r.fecha][k]) byDate[r.fecha][k] = [];
    if (r[colName] != null && !isNaN(r[colName])) {
      byDate[r.fecha][k].push(r[colName]);
    }
  });
  return Object.entries(byDate)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([fecha, entityData]) => {
      const row = { time: fecha.includes("T") ? fecha.slice(11, 16) : fecha };
      Object.entries(entityData).forEach(([k, vals]) => {
        const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
        row[k] = Number(metric.convert(mean).toFixed(4));
      });
      return row;
    });
}

function exportXlsx(filteredRows, metric, ventana, operators) {
  const colName = `${metric.key}_${ventana}_mean`;
  const data = filteredRows
    .filter((r) => r[colName] != null)
    .map((r) => {
      const op = operators.find((o) => o.id_operador === r.id_operador);
      return {
        fecha: r.fecha,
        operador: op?.nombre_operador ?? (typeof r.id_operador === "string" ? r.id_operador : `Op ${r.id_operador}`),
        [`${metric.key} (${metric.unit})`]: Number(metric.convert(r[colName]).toFixed(4)),
      };
    });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, metric.key);
  XLSX.writeFile(wb, `trafico_${metric.key}_${ventana}.xlsx`);
}

const downloadBtnSx = {
  border: "2px solid #003B8D",
  borderRadius: "4px",
  color: "#003B8D",
  backgroundColor: "#FFF",
  textTransform: "none",
  fontSize: "13px",
  padding: "6px 16px",
  fontWeight: 500,
  "&:hover": { backgroundColor: "#f0f4ff" },
};

// Leyenda con las series distribuidas en columnas y scroll vertical (slider)
// cuando hay muchas entradas, replicando el comportamiento de la leyenda de
// Plotly usada en "Calidad de datos". Recharts inyecta `payload`.
function ScrollableLegend({ payload = [], getLabel, hidden = {}, onToggle }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
        gap: "2px 14px",
        maxHeight: 84,
        overflowY: "auto",
        padding: "8px 8px 0",
        marginTop: 6,
        borderTop: "1px solid #f0f0f0",
      }}
    >
      {payload.map((entry) => {
        const key = entry.dataKey ?? entry.value;
        const isHidden = hidden[key];
        return (
          <div
            key={key}
            onClick={() => onToggle(key)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
              lineHeight: 1.7,
              minWidth: 0,
              fontSize: 10,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: entry.color,
                flexShrink: 0,
                opacity: isHidden ? 0.35 : 1,
              }}
            />
            <span
              style={{
                color: isHidden ? "#bbb" : "inherit",
                textDecoration: isHidden ? "line-through" : "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={getLabel(entry.value)}
            >
              {getLabel(entry.value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function TendenciasMetricas() {
  const [operators,    setOperators]    = useState([]);
  const [tecnologias,  setTecnologias]  = useState([]);
  const [apiData,      setApiData]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [reloadKey,    setReloadKey]    = useState(0);

  const [granularity,  setGranularity]  = useState("1d");
  const [periodoTab,   setPeriodoTab]   = useState("ultimo");
  const [fechaInicio,  setFechaInicio]  = useState(() => daysAgo(3));
  const [fechaFin,     setFechaFin]     = useState(() => toDateStr(new Date()));
  // Fechas en edición del "Periodo abierto"; se confirman con el botón Aplicar.
  const [draftInicio,  setDraftInicio]  = useState(() => daysAgo(3));
  const [draftFin,     setDraftFin]     = useState(() => toDateStr(new Date()));
  const [selectedOps,  setSelectedOps]  = useState([]);
  const [selectedZona, setSelectedZona] = useState(null);
  const [selectedTec,  setSelectedTec]  = useState(null);
  const [ventana,           setVentana]           = useState("dia");
  const [selectedDimension, setSelectedDimension] = useState("operador");
  const [filterTab,    setFilterTab]    = useState("grupo");
  const [rbdInput,     setRbdInput]     = useState("");
  const [selectedRbds, setSelectedRbds] = useState([]);

  // Load operators once
  useEffect(() => {
    axiosPrivate({ method: "GET", url: "/service/admin/get_operadores/", showSnackbarOnError: false })
      .then((res) => {
        const ops = res.data ?? [];
        setOperators(ops);
        setSelectedOps(ops.map((o) => o.id_operador));
      })
      .catch(() => {});
  }, []);

  // Load technology types once (igual que en FAS)
  useEffect(() => {
    api.getTechTypes()
      .then((res) => setTecnologias((res.data ?? []).map((t) => ({ id: t.id_tipo_tecnologia, label: t.nombre_enlace }))))
      .catch(() => {});
  }, []);

  const granConfig = GRAN_CONFIG[granularity];

  // Tracks which RBDs are already loaded for the current period signature.
  const rbdLoadRef = useRef({ sig: "", loaded: [] });

  // Fetch stats for GRUPO mode (single call replacing the dataset).
  useEffect(() => {
    if (filterTab !== "grupo") return;
    const cfg = GRAN_CONFIG[granularity];
    setLoading(true);
    setError(null);
    axiosPrivate({
      method: "GET",
      url: cfg.apiUrl,
      params: {
        granularidad: cfg.apiGran,
        dimension: selectedDimension,
        fecha_inicio: fechaInicio,
        fecha_fin:    fechaFin,
        ...(selectedZona != null && selectedZona !== "all" ? { id_zona:        Number(selectedZona) } : {}),
        ...(selectedTec  != null && selectedTec  !== "all" ? { tipo_tecnologia: Number(selectedTec) } : {}),
        ...(selectedOps.length === 1                       ? { id_operador:     selectedOps[0] }      : {}),
      },
      showSnackbarOnError: false,
    })
      .then((res) => setApiData((res.data ?? []).map((r) => ({ ...r, fecha: r.fecha_bucket }))))
      .catch(() => setError("Error al cargar los datos."))
      .finally(() => setLoading(false));
  }, [granularity, fechaInicio, fechaFin, selectedZona, selectedTec, selectedOps, selectedDimension, filterTab, reloadKey]);

  // Fetch stats for RBD mode, accumulating one call per RBD: adding an RBD
  // fetches only that one and appends it; removing one drops it from the
  // loaded data; changing period/granularity reloads all selected RBDs.
  useEffect(() => {
    if (filterTab !== "rbd") {
      rbdLoadRef.current = { sig: "", loaded: [] };
      return;
    }
    const cfg = GRAN_CONFIG[granularity];
    const sig = `${granularity}|${fechaInicio}|${fechaFin}`;
    const prev = rbdLoadRef.current;

    // La API cq espera el parámetro como "rbds"; stats-rbd/agregado usa "rbd".
    const rbdParamKey = cfg.apiUrl.includes("/cq") ? "rbds" : "rbd";
    const fetchRbd = (rbd) =>
      axiosPrivate({
        method: "GET",
        url: cfg.apiUrl,
        params: { granularidad: cfg.apiGran, dimension: "rbd", fecha_inicio: fechaInicio, fecha_fin: fechaFin, [rbdParamKey]: rbd },
        showSnackbarOnError: false,
      }).then((res) => (res.data ?? []).map((r) => ({ ...r, fecha: r.fecha_bucket })));

    if (selectedRbds.length === 0) {
      rbdLoadRef.current = { sig, loaded: [] };
      setApiData([]);
      setError(null);
      setLoading(false);
      return;
    }

    if (prev.sig !== sig) {
      // Period/granularity changed: reload every selected RBD from scratch.
      setLoading(true);
      setError(null);
      Promise.all(selectedRbds.map(fetchRbd))
        .then((results) => setApiData(results.flat()))
        .catch(() => setError("Error al cargar los datos."))
        .finally(() => setLoading(false));
      rbdLoadRef.current = { sig, loaded: [...selectedRbds] };
      return;
    }

    const added   = selectedRbds.filter((r) => !prev.loaded.includes(r));
    const removed  = prev.loaded.filter((r) => !selectedRbds.includes(r));
    if (removed.length) setApiData((data) => data.filter((row) => !removed.includes(String(row.rbd))));
    if (added.length) {
      setLoading(true);
      setError(null);
      Promise.all(added.map(fetchRbd))
        .then((results) => setApiData((data) => [...data, ...results.flat()]))
        .catch(() => setError("Error al cargar los datos."))
        .finally(() => setLoading(false));
    }
    rbdLoadRef.current = { sig, loaded: [...selectedRbds] };
  }, [filterTab, granularity, fechaInicio, fechaFin, selectedRbds, reloadKey]);

  const getEntityKey = useCallback((r) => {
    const tec = r.tipo_tecnologia != null ? String(r.tipo_tecnologia) : "";
    const dim = filterTab === "rbd" ? "rbd" : selectedDimension;
    switch (dim) {
      case "zona":         return String(r.id_zona ?? "");
      case "zona_tec":     return `${r.id_zona ?? ""}|${tec}`;
      case "rbd":          return String(r.rbd ?? "");
      case "operador":     return String(r.id_operador ?? "");
      case "operador_tec": return `${r.id_operador ?? ""}|${tec}`;
      case "global_tec":   return tec;
      default:             return "global";
    }
  }, [selectedDimension, filterTab]);

  const getEntityLabel = useCallback((key) => {
    const dim = filterTab === "rbd" ? "rbd" : selectedDimension;
    // La API cq entrega la tecnología ya como string real; sólo anteponemos
    // "Tec" cuando el valor es un id numérico que no está en el catálogo.
    const tecLabelOf = (tecId) => {
      const found = tecnologias.find((t) => String(t.id) === String(tecId));
      if (found) return found.label;
      return /^\d+$/.test(String(tecId)) ? `Tec ${tecId}` : String(tecId);
    };
    switch (dim) {
      case "zona":
        return MOCK_ZONAS.find((z) => String(z.id) === key)?.label ?? `Zona ${key}`;
      case "zona_tec": {
        const [zonaId, tecId] = key.split("|");
        const zonaLabel = MOCK_ZONAS.find((z) => String(z.id) === zonaId)?.label ?? `Zona ${zonaId}`;
        return `${zonaLabel} / ${tecLabelOf(tecId)}`;
      }
      case "rbd":
        return `RBD ${key}`;
      case "operador":
        return operators.find((o) => String(o.id_operador) === key)?.nombre_operador ?? key;
      case "operador_tec": {
        const [opKey, tecId] = key.split("|");
        const opLabel  = operators.find((o) => String(o.id_operador) === opKey)?.nombre_operador ?? opKey;
        return `${opLabel} / ${tecLabelOf(tecId)}`;
      }
      case "global_tec":
        return tecLabelOf(key);
      default:
        return "Global";
    }
  }, [selectedDimension, operators, filterTab, tecnologias]);

  const filteredRows = useMemo(() => {
    if (granularity === "15m" || granularity === "1h" || granularity === "1d") return apiData;
    if (filterTab === "rbd") {
      if (!selectedRbds.length) return apiData;
      return apiData.filter((r) => selectedRbds.includes(String(r.rbd)));
    }
    if (!selectedDimension.startsWith("operador")) return apiData;
    if (!selectedOps.length) return apiData;
    return apiData.filter((r) => selectedOps.includes(r.id_operador));
  }, [apiData, selectedOps, selectedDimension, granularity, filterTab, selectedRbds]);

  const entityKeys = useMemo(
    () => [...new Set(filteredRows.map(getEntityKey).filter(Boolean))],
    [filteredRows, getEntityKey]
  );

  const entityColorMap = useMemo(() => {
    const m = {};
    entityKeys.forEach((k, i) => { m[k] = OP_COLORS[i % OP_COLORS.length]; });
    return m;
  }, [entityKeys]);

  // Las fechas del periodo abierto son de libre selección hasta hoy; la ventana
  // máxima permitida se valida aparte (periodoError) y bloquea el botón Aplicar.
  const maxFechaFin = useMemo(() => toDateStr(new Date()), []);

  // Valida la ventana del periodo abierto: 3 meses para 1d, 6 para 7d/30d.
  const periodoError = useMemo(() => {
    if (periodoTab !== "abierto") return null;
    if (granularity === "15m" || granularity === "1h") return null;
    if (draftFin < draftInicio) return "La fecha 'Hasta' no puede ser anterior a 'Desde'.";
    if (granularity === "1d") {
      const lim = new Date(draftInicio + "T12:00:00");
      lim.setMonth(lim.getMonth() + 3);
      if (new Date(draftFin + "T12:00:00") > lim) {
        return "El rango no puede superar los 3 meses para granularidad de 1 día.";
      }
    } else if (monthsBetween(draftInicio, draftFin) > 6) {
      return "El rango no puede superar los 6 meses para esta granularidad.";
    }
    return null;
  }, [periodoTab, granularity, draftInicio, draftFin]);

  // Confirma las fechas en edición del periodo abierto (dispara la carga).
  const handleAplicarPeriodo = useCallback(() => {
    setFechaInicio(draftInicio);
    setFechaFin(draftFin);
  }, [draftInicio, draftFin]);

  const periodoDirty = draftInicio !== fechaInicio || draftFin !== fechaFin;

  const handleClearFilters = useCallback(() => {
    const { ini, fin } = GRAN_CONFIG["1d"].getDefaultRange();
    setGranularity("1d");
    setPeriodoTab("ultimo");
    setFechaInicio(ini);
    setFechaFin(fin);
    setDraftInicio(ini);
    setDraftFin(fin);
    setSelectedOps(operators.map((o) => o.id_operador));
    setSelectedZona(null);
    setSelectedTec(null);
    setVentana("dia");
    setSelectedDimension("operador");
    setFilterTab("grupo");
    setRbdInput("");
    setSelectedRbds([]);
  }, [operators]);

  // Reintenta la carga: resetea el control de RBD cargados para forzar una
  // recarga completa y dispara de nuevo el useEffect correspondiente.
  const handleRetry = useCallback(() => {
    rbdLoadRef.current = { sig: "", loaded: [] };
    setReloadKey((k) => k + 1);
  }, []);

  const handleAddRbd = useCallback(() => {
    const val = rbdInput.trim();
    if (!val || selectedRbds.includes(val) || selectedRbds.length >= MAX_RBDS) return;
    setSelectedRbds((prev) => [...prev, val]);
    setRbdInput("");
  }, [rbdInput, selectedRbds]);

  const handleRemoveRbd = useCallback((rbd) => {
    setSelectedRbds((prev) => prev.filter((r) => r !== rbd));
  }, []);

  // Removes an entity only from the loaded data (no re-fetch).
  const handleRemoveEntity = useCallback((key) => {
    setApiData((prev) => prev.filter((r) => getEntityKey(r) !== key));
  }, [getEntityKey]);

  const operadorOpts = useMemo(() => [
    { value: "all", label: "Todos los operadores" },
    ...operators.map((o) => ({ value: String(o.id_operador), label: o.nombre_operador })),
  ], [operators]);

  const opFilterValue = !selectedOps.length || selectedOps.length === operators.length
    ? "all"
    : String(selectedOps[0] ?? "all");

  const dimDisabled = DIM_DISABLED[selectedDimension] ?? NO_DISABLED;

  // Resetea los filtros que quedan desactivados al cambiar de dimensión,
  // para que no sigan afectando la consulta.
  useEffect(() => {
    const d = DIM_DISABLED[selectedDimension] ?? NO_DISABLED;
    if (d.operador) setSelectedOps(operators.map((o) => o.id_operador));
    if (d.zona) setSelectedZona(null);
    if (d.tec) setSelectedTec(null);
  }, [selectedDimension, operators]);

  const opColorMap = useMemo(() => {
    const m = {};
    operators.forEach((o, i) => { m[o.id_operador] = OP_COLORS[i % OP_COLORS.length]; });
    return m;
  }, [operators]);

  const [hiddenLines, setHiddenLines] = useState({});

  const toggleLine = useCallback((key) => {
    setHiddenLines((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return (
    <PageBody>
      <Box sx={{ mx: "-3.5rem", display: "flex", gap: 2 }}>

        {/* ── Sidebar ── */}
        <Box sx={{ width: 300, flexShrink: 0, position: "sticky", top: 16, alignSelf: "flex-start" }}>
          <Paper sx={{ p: 1.5, maxHeight: "calc(100vh - 32px)", overflowY: "auto", overflowX: "hidden",
            pointerEvents: loading ? "none" : "auto", opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}>

            {/* ── Filtro de tiempo ── */}
            <Typography variant="h5" mb={1.5}>Filtro de tiempo</Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Granularidad</Typography>
              <MuiTooltip title={GRAN_HELP} arrow placement="top"
                componentsProps={{
                  tooltip: { sx: { bgcolor: "#003B8D", color: "#fff", fontSize: "0.72rem", fontWeight: 500, borderRadius: "8px", px: 1.25, py: 0.75 } },
                  arrow: { sx: { color: "#003B8D" } },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 15, color: "action.active", cursor: "help" }} />
              </MuiTooltip>
            </Box>
            <Box sx={{ display: "flex", gap: 0, mb: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
              {Object.entries(GRAN_CONFIG).map(([key, cfg], i, arr) => (
                <Button key={key} size="small" disableRipple
                  variant={granularity === key ? "contained" : "text"} color="primary"
                  onClick={() => { const { ini, fin } = GRAN_CONFIG[key].getDefaultRange(); setGranularity(key); setFechaInicio(ini); setFechaFin(fin); setDraftInicio(ini); setDraftFin(fin); }}
                  sx={{ flex: 1, px: 0.3, py: 1, fontSize: "0.68rem", textTransform: "none", borderRadius: 0, borderRight: i < arr.length - 1 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none", minWidth: 0 }}
                >{cfg.label}</Button>
              ))}
            </Box>

            <Tabs value={periodoTab} onChange={(_, v) => { setPeriodoTab(v); if (v === "abierto") { setDraftInicio(fechaInicio); setDraftFin(fechaFin); } }}
              variant="fullWidth"
              sx={{ mb: 1.5, minHeight: 40, borderBottom: "1px solid", borderColor: "divider",
                "& .MuiTabs-scroller": { minHeight: 40 },
                "& .MuiTab-root": { minHeight: 40, py: 1, fontSize: "0.75rem", fontWeight: "bold", textTransform: "uppercase", color: "text.secondary" },
                "& .Mui-selected": { bgcolor: "rgba(25,118,210,0.06)" },
              }}
            >
              <Tab label="Último periodo" value="ultimo" disableRipple />
              <Tab label="Periodo abierto" value="abierto" disableRipple />
            </Tabs>

            {periodoTab === "ultimo" ? (
              <Box sx={{ display: "flex", gap: 0, mb: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                {granConfig.presets.map((p, i) => {
                  const { ini, fin } = p.compute();
                  return (
                    <Button key={p.label} size="small" disableRipple
                      variant={fechaInicio === ini && fechaFin === fin ? "contained" : "text"} color="primary"
                      onClick={() => { setFechaInicio(ini); setFechaFin(fin); }}
                      sx={{ flex: 1, px: 0.3, py: 1, fontSize: "0.68rem", textTransform: "none", borderRadius: 0, borderRight: i < granConfig.presets.length - 1 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none", minWidth: 0 }}
                    >{p.label}</Button>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ mb: 1.5 }}>
                {(granularity === "15m" || granularity === "1h") && (
                  <TextField label="Fecha" type="date" size="small" fullWidth
                    value={draftInicio} onChange={(e) => { setDraftInicio(e.target.value); setDraftFin(e.target.value); }}
                    InputLabelProps={{ shrink: true }} inputProps={{ max: toDateStr(new Date()) }}
                  />
                )}
                {granularity === "1d" && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField label="Desde" type="date" size="small" fullWidth value={draftInicio}
                      onChange={(e) => setDraftInicio(e.target.value)}
                      InputLabelProps={{ shrink: true }} inputProps={{ max: maxFechaFin }}
                    />
                    <TextField label="Hasta" type="date" size="small" fullWidth value={draftFin}
                      onChange={(e) => setDraftFin(e.target.value)}
                      InputLabelProps={{ shrink: true }} inputProps={{ max: maxFechaFin }}
                    />
                  </Box>
                )}
                {(granularity === "7d" || granularity === "30d") && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField label="Desde" type="month" size="small" fullWidth value={draftInicio.slice(0, 7)}
                      onChange={(e) => setDraftInicio(e.target.value + "-01")}
                      InputLabelProps={{ shrink: true }} inputProps={{ max: maxFechaFin.slice(0, 7) }}
                    />
                    <TextField label="Hasta" type="month" size="small" fullWidth value={draftFin.slice(0, 7)}
                      onChange={(e) => { const last = monthLastDay(e.target.value); setDraftFin(last > maxFechaFin ? maxFechaFin : last); }}
                      InputLabelProps={{ shrink: true }} inputProps={{ max: maxFechaFin.slice(0, 7) }}
                    />
                  </Box>
                )}
                {periodoError && (
                  <Alert severity="error" sx={{ mt: 1, py: 0, fontSize: "0.72rem", alignItems: "center", "& .MuiAlert-message": { py: 0.75 } }}>
                    {periodoError}
                  </Alert>
                )}
                <Button variant="contained" color="primary" size="small" fullWidth disableRipple disableElevation
                  onClick={handleAplicarPeriodo} disabled={!periodoDirty || !!periodoError}
                  sx={{ mt: 1.25, textTransform: "none", fontWeight: "bold" }}
                >
                  Aplicar
                </Button>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* ── Filtro por grupo o RBD ── */}
            <Typography variant="h5" mb={1}>Filtro por grupo o RBD</Typography>

            <Tabs value={filterTab} onChange={(_, v) => setFilterTab(v)}
              variant="fullWidth"
              sx={{ mb: 1.5, minHeight: 40, borderBottom: "1px solid", borderColor: "divider",
                "& .MuiTabs-scroller": { minHeight: 40 },
                "& .MuiTab-root": { minHeight: 40, py: 1, fontSize: "0.75rem", fontWeight: "bold", textTransform: "uppercase", color: "text.secondary" },
                "& .Mui-selected": { bgcolor: "rgba(25,118,210,0.06)" },
              }}
            >
              <Tab label="GRUPO" value="grupo" disableRipple />
              <Tab label="RBD" value="rbd" disableRipple />
            </Tabs>

            {filterTab === "grupo" ? (
              <>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Dimensión</Typography>
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: "flex", border: "1px solid", borderColor: "divider", borderRadius: "4px 4px 0 0", overflow: "hidden", borderBottom: "none" }}>
                    {GROUP_DIM_OPTIONS.slice(0, 3).map((d, i) => (
                      <Button key={d.key} size="small" disableRipple
                        variant={selectedDimension === d.key ? "contained" : "text"} color="primary"
                        onClick={() => setSelectedDimension(d.key)}
                        sx={{ flex: 1, px: 0.3, py: 0.75, fontSize: "0.65rem", textTransform: "none", borderRadius: 0, borderRight: i < 2 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none", minWidth: 0 }}
                      >{d.label}</Button>
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", border: "1px solid", borderColor: "divider", borderRadius: "0 0 4px 4px", overflow: "hidden" }}>
                    {GROUP_DIM_OPTIONS.slice(3).map((d, i) => (
                      <Button key={d.key} size="small" disableRipple
                        variant={selectedDimension === d.key ? "contained" : "text"} color="primary"
                        onClick={() => setSelectedDimension(d.key)}
                        sx={{ flex: 1, px: 0.3, py: 0.75, fontSize: "0.65rem", textTransform: "none", borderRadius: 0, borderRight: i < 2 ? "1px solid" : "none", borderColor: "divider", boxShadow: "none", minWidth: 0 }}
                      >{d.label}</Button>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <InputFilter item="operador" name="Operadores" opt={operadorOpts} value={opFilterValue}
                    disabled={dimDisabled.operador}
                    callback={(_, val) => { if (!val || val === "all") setSelectedOps(operators.map((o) => o.id_operador)); else setSelectedOps([Number(val)]); }}
                  />
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <InputFilter item="zona" name="Zona" noSort
                    disabled={dimDisabled.zona}
                    opt={[{ value: "all", label: "Todas las zonas" }, ...MOCK_ZONAS.map((z) => ({ value: String(z.id), label: z.label }))]}
                    value={selectedZona} callback={(_, val) => setSelectedZona(val ?? null)}
                  />
                </Box>
                <Box sx={{ mb: 1.5 }}>
                  <InputFilter item="tecnologia" name="Tecnología"
                    disabled={dimDisabled.tec}
                    opt={[{ value: "all", label: "Todas las tecnologías" }, ...tecnologias.map((t) => ({ value: String(t.id), label: t.label }))]}
                    value={selectedTec} callback={(_, val) => setSelectedTec(val ?? null)}
                  />
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
                  {entityKeys.map((k) => (
                    <Chip key={k} label={getEntityLabel(k)} size="small"
                      onDelete={() => handleRemoveEntity(k)}
                      sx={{ fontSize: "0.68rem", bgcolor: "white", border: "1px solid #1976D2", "& .MuiChip-deleteIcon": { color: "#1976D2", "&:hover": { color: "#1565C0" } } }}
                    />
                  ))}
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
                  <TextField size="small" fullWidth label="RBD" value={rbdInput}
                    onChange={(e) => setRbdInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleAddRbd(); }}
                    InputProps={{ startAdornment: <TuneIcon fontSize="small" sx={{ color: "action.active", mr: 1 }} /> }}
                  />
                  <IconButton onClick={handleAddRbd} color="primary"
                    disabled={!rbdInput || selectedRbds.length >= MAX_RBDS}
                  >
                    <AddCircleIcon />
                  </IconButton>
                </Box>
                {selectedRbds.length > 0 && (
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0.75, mb: 1, p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
                    {selectedRbds.map((rbd) => (
                      <Chip key={rbd} label={rbd} size="small"
                        deleteIcon={<RemoveCircleOutlineIcon fontSize="small" />}
                        onDelete={() => handleRemoveRbd(rbd)}
                        sx={{ width: "100%", justifyContent: "space-between", fontSize: "0.75rem", fontWeight: 500, bgcolor: "#E3F0FB", color: "#0F69C4", borderRadius: "16px", "& .MuiChip-deleteIcon": { color: "#0F69C4", "&:hover": { color: "#0b4f96" } } }}
                      />
                    ))}
                  </Box>
                )}
                {selectedRbds.length >= MAX_RBDS && (
                  <Typography variant="caption" color="error">Límite completo de RBD a comparar</Typography>
                )}
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* ── Ventana ── */}
            {SHOW_VENTANA_FILTER && (
              <>
                <Typography variant="h5" mb={1}>Ventana</Typography>
                <RadioGroup value={ventana} onChange={(e) => setVentana(e.target.value)} sx={{ mb: 1.5 }}>
                  <FormControlLabel value="ven" control={<Radio size="small" />} label={<Typography variant="body2">Ventana Horaria</Typography>} />
                  <FormControlLabel value="dia" control={<Radio size="small" />} label={<Typography variant="body2">Día completo</Typography>} />
                </RadioGroup>

                <Divider sx={{ my: 2 }} />
              </>
            )}

            <Button variant="outlined" color="error" size="small" fullWidth disableRipple
              startIcon={<RemoveCircleOutlineIcon />} onClick={handleClearFilters}
              sx={{ textTransform: "none" }}
            >
              Limpiar todos los filtros
            </Button>
          </Paper>
        </Box>

        {/* ── Charts ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <HourglassLoader />
          ) : error ? (
            <Paper sx={{ p: 4, minHeight: 200, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 2.5 }}>
              <Alert
                severity="error"
                sx={{ width: "100%", fontSize: "1rem", alignItems: "center", "& .MuiAlert-message": { textAlign: "center", width: "100%" } }}
              >
                {error}
              </Alert>
              <PageButtonBlack onClick={handleRetry} icon={<ReplayIcon />} width="220px">
                Reintentar
              </PageButtonBlack>
            </Paper>
          ) : filterTab === "rbd" && selectedRbds.length === 0 ? (
            <Paper sx={{ p: 4, minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Typography color="text.secondary">Seleccione al menos un RBD para visualizar datos.</Typography>
            </Paper>
          ) : filteredRows.length === 0 ? (
            <Paper sx={{ p: 4, minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Typography color="text.secondary">Sin datos para los filtros seleccionados.</Typography>
            </Paper>
          ) : (
            METRICS.map((metric) => {
              const chartData = buildChartData(filteredRows, metric, ventana, getEntityKey);
              return (
                <Paper key={metric.key} sx={{ p: 3.5, mb: 2 }}>
                  {/* Fila 1: título (izquierda) + descripción (derecha) */}
                  <Grid2 container spacing={2} alignItems="flex-start" mb={3}>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                      <Typography
                        variant="h5"
                        sx={{ textTransform: "uppercase", lineHeight: 1.3 }}
                      >
                        {metric.label}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 8 }}>
                      <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
                        {metric.description}
                      </Typography>
                    </Grid2>
                  </Grid2>

                  {/* Fila 2: gráfico ancho completo */}
                  <ResponsiveContainer width="100%" height={230}>
                    <LineChart data={chartData} margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="time"
                        tick={{ fontSize: 9 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis
                        tick={{ fontSize: 9 }}
                        tickFormatter={(v) => Number(v).toFixed(2)}
                        unit={` ${metric.unit}`}
                        width={70}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          `${Number(value).toFixed(2)} ${metric.unit}`,
                          getEntityLabel(name),
                        ]}
                      />
                      <Legend
                        content={
                          <ScrollableLegend
                            getLabel={getEntityLabel}
                            hidden={hiddenLines}
                            onToggle={toggleLine}
                          />
                        }
                      />
                      {entityKeys.map((k) => (
                        <Line
                          key={k}
                          type="monotone"
                          dataKey={k}
                          hide={hiddenLines[k] ?? false}
                          stroke={entityColorMap[k]}
                          dot={{ r: 3, fill: entityColorMap[k] }}
                          activeDot={{ r: 5 }}
                          strokeWidth={2}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>

                  <Divider sx={{ mt: 1.5, mb: 2 }} />

                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={() => exportXlsx(filteredRows, metric, ventana, operators)} sx={downloadBtnSx}>
                      Descarga tabla (.csv)
                    </Button>
                  </Box>
                </Paper>
              );
            })
          )}
        </Box>

      </Box>
    </PageBody>
  );
}
